import type {
  ClassCategory,
  ProfileClassCategoryGroup,
  ProfileClassOrganizationLayout,
} from '@/features/profile/classes/classCategoryTypes';
import type { BoardJoinStatus, OwnedClassCard } from '@/features/profile/types';
import { registerBoard } from '@/features/class/classRegistry';

const CLASS_DOT_PALETTE = [
  'bg-[#f87171]',
  'bg-[#fb923c]',
  'bg-[#fbbf24]',
  'bg-[#34d399]',
  'bg-[#60a5fa]',
  'bg-[#c084fc]',
  'bg-[#f472b6]',
] as const;

function dotForBoardId(id: number): string {
  const safe = Number.isFinite(id) ? Math.abs(id) : 0;
  return CLASS_DOT_PALETTE[safe % CLASS_DOT_PALETTE.length];
}

interface RawBoard {
  id: number | string;
  createdBy?: number | string | { id?: number | string; userId?: number | string };
  owner?: number | string | { id?: number | string; userId?: number | string };
  ownerId?: number | string;
  userId?: number | string;
  code: string;
  name: string;
  isPrivate?: boolean;
  isJoined?: number | string;
  joinBoardCount?: number;
  newsPostCount?: number;
  position?: number;
}

function normalizeUserId(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return normalizeUserId(record.id) ?? normalizeUserId(record.userId);
  }
  return undefined;
}

interface RawCategoryGroup {
  categoryId?: number | null;
  categoryName?: string | null;
  boards?: unknown;
  archive?: boolean;
}

function isRawBoard(value: unknown): value is RawBoard {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (typeof v.id === 'number' || typeof v.id === 'string') &&
    typeof v.code === 'string' &&
    typeof v.name === 'string'
  );
}

function normalizeBoardJoinStatus(value: unknown): BoardJoinStatus | undefined {
  const status =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return status === 0 || status === 1 || status === 2 ? status : undefined;
}

function toOwnedCard(raw: RawBoard): OwnedClassCard {
  const id = typeof raw.id === 'string' ? Number(raw.id) : raw.id;
  registerBoard({ id: String(id), code: raw.code, name: raw.name });
  return {
    id: String(id),
    ownerId:
      normalizeUserId(raw.createdBy) ??
      normalizeUserId(raw.owner) ??
      normalizeUserId(raw.ownerId) ??
      normalizeUserId(raw.userId),
    code: raw.code,
    name: raw.name,
    postCount: typeof raw.newsPostCount === 'number' ? raw.newsPostCount : 0,
    memberCount: typeof raw.joinBoardCount === 'number' ? raw.joinBoardCount : 0,
    isPrivate: Boolean(raw.isPrivate),
    isJoined: normalizeBoardJoinStatus(raw.isJoined),
    dotClassName: dotForBoardId(id),
  };
}

function byBoardPosition(a: RawBoard, b: RawBoard): number {
  const ap = typeof a.position === 'number' ? a.position : Number.MAX_SAFE_INTEGER;
  const bp = typeof b.position === 'number' ? b.position : Number.MAX_SAFE_INTEGER;
  if (ap !== bp) return ap - bp;
  return a.name.localeCompare(b.name);
}

const EMPTY_LAYOUT: ProfileClassOrganizationLayout = {
  uncategorized: [],
  categories: [],
  totalCount: 0,
};

/**
 * Build a profile classes layout from a `boardWithCategory` array — used for
 * both the logged-in user (`rawUser.boardWithCategory`) and other users
 * (`GET /userservice/api/v3/user/:id` response).
 */
export function buildLayoutFromBoardWithCategory(
  groupsInput: unknown,
): ProfileClassOrganizationLayout {
  if (!Array.isArray(groupsInput) || groupsInput.length === 0) return EMPTY_LAYOUT;

  const isUncategorisedGroup = (group: RawCategoryGroup): boolean =>
    group.categoryName == null || group.categoryId == null || group.categoryId === -1;

  const seen = new Set<string>();
  const uncategorized: OwnedClassCard[] = [];
  const categories: ProfileClassCategoryGroup[] = [];
  let sortOrder = 0;

  for (const groupRaw of groupsInput) {
    if (!groupRaw || typeof groupRaw !== 'object') continue;
    const group = groupRaw as RawCategoryGroup;
    if (group.archive || isUncategorisedGroup(group)) continue;

    const boards = Array.isArray(group.boards)
      ? group.boards.filter(isRawBoard).slice().sort(byBoardPosition)
      : [];

    const cards: OwnedClassCard[] = [];
    for (const board of boards) {
      const card = toOwnedCard(board);
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      cards.push(card);
    }
    if (cards.length === 0) continue;

    const category: ClassCategory = {
      id: String(group.categoryId),
      name: String(group.categoryName),
      classIds: cards.map((c) => c.id),
      sortOrder: sortOrder++,
    };
    categories.push({ category, classes: cards });
  }

  for (const groupRaw of groupsInput) {
    if (!groupRaw || typeof groupRaw !== 'object') continue;
    const group = groupRaw as RawCategoryGroup;
    if (group.archive || !isUncategorisedGroup(group)) continue;

    const boards = Array.isArray(group.boards)
      ? group.boards.filter(isRawBoard).slice().sort(byBoardPosition)
      : [];
    for (const board of boards) {
      const card = toOwnedCard(board);
      if (seen.has(card.id)) continue;
      seen.add(card.id);
      uncategorized.push(card);
    }
  }

  const totalCount =
    uncategorized.length + categories.reduce((sum, group) => sum + group.classes.length, 0);

  return { uncategorized, categories, totalCount };
}

/** Convenience wrapper for the login `rawUser` object. */
export function buildLayoutFromRawUser(rawUser: unknown): ProfileClassOrganizationLayout {
  if (!rawUser || typeof rawUser !== 'object') return EMPTY_LAYOUT;
  const groups = (rawUser as Record<string, unknown>).boardWithCategory;
  return buildLayoutFromBoardWithCategory(groups);
}
